import React, { useEffect, useRef, useState } from 'react';
import { TemplateRenderer } from '../../components/PortfolioTemplates';
import { useUser } from '../../contexts/UserContext';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import FreeCreditsLabel from '../../components/FreeCreditsLabel';
import {
  LayoutGridIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SendIcon,
  LinkIcon,
  XIcon,
  SparklesIcon,
} from '../icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* ─────────────────────────────────────────────────────────────────────────
 * Portfolio Builder — Electron-native pane.
 *
 * One screen: template grid on the left, live preview on the right.
 * Reuses the website's <TemplateRenderer /> so all 6 portfolio templates
 * (Minimal / Dark Pro / Creative / Executive / Neon / Elegant) render
 * identically to the public /p/:slug page — what the recruiter sees
 * matches the desktop preview byte-for-byte.
 *
 * Backend endpoints (same as the website):
 *   GET  /api/profile
 *   GET  /api/portfolio/me
 *   POST /api/portfolio/save
 *   PATCH /api/profile/portfolio-url
 *
 * Subscription gate: saving works for everyone, but the public share link
 * + "Open public preview" only show for subscribers. Non-subscribers get
 * an inline upsell card that fires the shared `openPricingModal` event,
 * which ElectronShell turns into a BillingModal open.
 * ─────────────────────────────────────────────────────────────────────── */

const TEMPLATES = [
  { id: 'minimal',   name: 'Minimal',   desc: 'Clean & typographic',  bg: '#f8f8f8',  accent: '#111' },
  { id: 'dark',      name: 'Dark Pro',  desc: 'Sleek glassmorphism',  bg: '#0a0a12',  accent: '#a78bfa' },
  { id: 'creative',  name: 'Creative',  desc: 'Purple sidebar',       bg: 'linear-gradient(180deg,#1e1148,#6d28d9)', accent: '#fff' },
  { id: 'executive', name: 'Executive', desc: 'Two-column corporate', bg: '#1e2a4a',  accent: '#4fc3f7' },
  { id: 'neon',      name: 'Neon',      desc: 'Cyberpunk tech',       bg: '#050a10',  accent: '#00e5ff' },
  { id: 'elegant',   name: 'Elegant',   desc: 'Serif & gold accents', bg: '#faf8f5',  accent: '#b8860b' },
];

export default function PortfolioBuilderPane() {
  const { user } = useUser();
  const fetchedRef = useRef(false);

  const [profile, setProfile] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('minimal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  // Subscribers see the public share link + open-preview button. Same
  // gate as the website (hasAccess on PortfolioPage.jsx:33).
  const hasAccess = !!(
    user?.subscription_status === 'active' ||
    user?.subscription_status === 'cancelling' ||
    user?.subscription_plan
  );

  const publicLink = portfolioData?.slug
    ? `${window.location.origin}/p/${portfolioData.slug}`
    : null;

  useEffect(() => {
    if (!user || fetchedRef.current) return;
    fetchedRef.current = true;

    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${API_BASE_URL}/api/profile`,      { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE_URL}/api/portfolio/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ])
      .then(([profileRes, portfolioRes]) => {
        if (profileRes?.profile) setProfile(profileRes.profile);
        if (portfolioRes?.portfolio) {
          setPortfolioData(portfolioRes.portfolio);
          if (portfolioRes.portfolio.template) {
            setSelectedTemplate(portfolioRes.portfolio.template);
          }
        }
      })
      .catch(err => console.error('Portfolio fetch failed:', err))
      .finally(() => setLoading(false));
  }, [user]);

  const gate = useFeatureGate('portfolioBuilder');

  const handleSave = async () => {
    if (saving) return;
    if (gate.locked) { gate.openPaywall(); return; }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      // Per-feature credit gate via the shared hook — refreshes the
      // counter chip in the header and opens the paywall on 403.
      const { ok, reason } = await gate.deduct();
      if (!ok) {
        if (reason === 'no-credits') { setSaving(false); return; }
        throw new Error(reason || 'Out of Portfolio Builder credits — upgrade to continue.');
      }
      const res = await fetch(`${API_BASE_URL}/api/portfolio/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ template: selectedTemplate }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || data?.message || `Save failed (${res.status})`);

      setPortfolioData(data.portfolio);

      // Auto-write the public URL to the user's profile, ONLY for paid
      // users — free users can save a portfolio but the shareable link
      // stays locked until they subscribe.
      if (data.portfolio?.slug && hasAccess) {
        const publicUrl = `${window.location.origin}/p/${data.portfolio.slug}`;
        fetch(`${API_BASE_URL}/api/profile/portfolio-url`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ portfolioUrl: publicUrl }),
        }).catch(() => {});
      }

      if (hasAccess) {
        setShowSuccess(true);
      } else {
        // Free-tier path — open billing instead of showing the success popup,
        // matching the website's `openPricingRef.current?.()` behavior.
        document.dispatchEvent(new CustomEvent('openPricingModal'));
      }
    } catch (err) {
      setError(err.message || 'Failed to save portfolio.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard may not be available */ }
  };

  const openPublicPreview = () => {
    if (!publicLink) return;
    // window.open is available in Electron renderer; BrowserWindow handles
    // it via the `new-window` / `setWindowOpenHandler` event in main.js.
    window.open(publicLink, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="pane">
        <div className="pane-eyebrow"><LayoutGridIcon size={11} /> Portfolio</div>
        <h1 className="pane-title">Loading your portfolio…</h1>
        <p className="pane-subtitle">Pulling profile data and any previously saved template.</p>
      </div>
    );
  }

  return (
    <div className="pbp-root">
      {/* Page header */}
      <div className="pane">
        <div className="pane-eyebrow"><LayoutGridIcon size={11} /> Portfolio Builder</div>
        <h1 className="pane-title">Public Portfolio</h1>
      </div>

      <div className="pbp-workspace">
        {/* LEFT: template picker + save */}
        <div className="pbp-left">
          <div className="pane-card">
            <div className="pbp-section-head">
              <span className="pane-label">Choose a style</span>
              <span className="pane-hint">{TEMPLATES.length} templates · live preview</span>
            </div>

            <div className="pbp-template-grid">
              {TEMPLATES.map(t => {
                const active = selectedTemplate === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`pbp-tpl ${active ? 'active' : ''}`}
                    onClick={() => setSelectedTemplate(t.id)}
                  >
                    <div className="pbp-tpl-thumb" style={{ background: t.bg }}>
                      <div className="pbp-tpl-line" style={{ background: t.accent, width: '60%', opacity: 0.95 }} />
                      <div className="pbp-tpl-line" style={{ background: t.accent, width: '38%', opacity: 0.55 }} />
                      <div className="pbp-tpl-line" style={{ background: t.accent, width: '78%', opacity: 0.32 }} />
                      <div className="pbp-tpl-line" style={{ background: t.accent, width: '64%', opacity: 0.32 }} />
                    </div>
                    <div className="pbp-tpl-meta">
                      <div className="pbp-tpl-name">
                        {t.name}
                        {active && <span className="pbp-tpl-check"><CheckCircleIcon size={12} /></span>}
                      </div>
                      <div className="pbp-tpl-desc">{t.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="pane-error">{error}</div>}

          <button
            type="button"
            className="pane-cta pbp-save-btn"
            onClick={handleSave}
            disabled={saving || gate.locked}
            title={gate.locked ? 'Out of free credits — upgrade to keep using' : ''}
          >
            {saving
              ? 'Saving…'
              : <>Save Portfolio <SendIcon size={13} /> <FreeCreditsLabel feature="portfolioBuilder" /></>}
          </button>

          {/* Already-published share card */}
          {portfolioData?.slug && hasAccess && (
            <div className="pbp-share-card">
              <div className="pbp-share-head">
                <CheckCircleIcon size={13} />
                <span>Published</span>
              </div>
              <div className="pbp-share-link">
                <span className="pbp-share-url">{publicLink}</span>
                <button type="button" className="pbp-share-copy" onClick={handleCopyLink}>
                  {copied
                    ? <><CheckCircleIcon size={11} /> Copied</>
                    : <><LinkIcon size={11} /> Copy</>}
                </button>
              </div>
              <button type="button" className="pane-cta-secondary pbp-share-open" onClick={openPublicPreview}>
                Open public preview ↗
              </button>
            </div>
          )}

          {/* Upsell card for non-subscribers */}
          {portfolioData?.slug && !hasAccess && (
            <div className="pbp-upsell-card">
              <div className="pbp-upsell-icon"><SparklesIcon size={18} /></div>
              <div>
                <div className="pbp-upsell-title">Upgrade to share publicly</div>
                <p className="pbp-upsell-body">
                  Your portfolio is saved. Subscribe to publish it at a public link recruiters can open.
                </p>
              </div>
              <button
                type="button"
                className="pane-cta"
                onClick={() => document.dispatchEvent(new CustomEvent('openPricingModal'))}
              >
                See plans
              </button>
            </div>
          )}

          {!profile && (
            <div className="pbp-empty-profile">
              <AlertTriangleIcon size={14} />
              <div>
                Your profile is empty. Fill out{' '}
                <strong>Profile</strong> from the avatar menu to make this preview meaningful.
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: live preview frame */}
        <div className="pbp-right">
          <div className="pbp-preview-card">
            <div className="pbp-preview-head">
              <div>
                <div className="pbp-preview-eyebrow">Live preview</div>
                <div className="pbp-preview-title">
                  {TEMPLATES.find(t => t.id === selectedTemplate)?.name}
                </div>
              </div>
              <div className="pbp-preview-tag">{selectedTemplate}</div>
            </div>
            <div className="pbp-preview-frame">
              {profile ? (
                <TemplateRenderer template={selectedTemplate} profile={profile} />
              ) : (
                <div className="pbp-preview-empty">
                  <LayoutGridIcon size={28} />
                  <h3>Profile is empty</h3>
                  <p>
                    Add at least a name, summary, and one experience entry under Profile and the preview will populate.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success modal */}
      {showSuccess && (
        <div className="pbp-modal-bg" onClick={() => setShowSuccess(false)}>
          <div className="pbp-modal" onClick={e => e.stopPropagation()}>
            <div className="pbp-modal-head">
              <h3>Portfolio saved</h3>
              <button
                type="button"
                className="ilv-modal-close"
                onClick={() => setShowSuccess(false)}
                aria-label="Close"
              >
                <XIcon size={18} />
              </button>
            </div>
            <p className="pbp-modal-sub">Share this link with recruiters — it works without an account.</p>
            <div className="pbp-modal-link-row">
              <span className="pbp-modal-link">{publicLink}</span>
              <button
                type="button"
                className="pbp-modal-copy"
                onClick={handleCopyLink}
              >
                {copied
                  ? <><CheckCircleIcon size={12} /> Copied</>
                  : <><LinkIcon size={12} /> Copy</>}
              </button>
            </div>
            <div className="pbp-modal-actions">
              <button
                type="button"
                className="pane-cta-secondary"
                onClick={() => setShowSuccess(false)}
              >
                Done
              </button>
              {publicLink && (
                <button type="button" className="pane-cta" onClick={openPublicPreview}>
                  Open public preview ↗
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
